"use client"

import { trackFactFinderAddToCartAction, trackFactFinderProductClickAction } from "@/actions/factFinderTrackingActions"
import { sendGTMEvent } from "@next/third-parties/google"
import { ProductPriceContainerCtnrROV1 } from "@ub/pricing-client"

import { CardPrices, determineCardPrices } from "@/lib/price-utils"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { calculateCategoryPathString, determineEffectiveProductId } from "@/lib/storefront/product/productHelper"

export function trackAddToBasket(
  isoLocale: string,
  isB2B: boolean,
  product: ReducedProductData | undefined,
  prices: ProductPriceContainerCtnrROV1 | undefined,
  quantity: number
) {
  if (product && prices && quantity) {
    const cardPrices: CardPrices = determineCardPrices(isoLocale, isB2B, prices, undefined, quantity, product.id)

    const addToCartData = {
      event: "addToCart",
      ecommerce: {
        currencyCode: product.currency,
        add: {
          products: [
            {
              name: product.title,
              id: determineEffectiveProductId(product.id),
              price: cardPrices.salePrice,
              brand: product.brand,
              category: calculateCategoryPathString(product),
              quantity: "" + quantity,
            },
          ],
        },
      },
    }

    // Send it to Google Tag Manager
    sendGTMEvent(addToCartData)

    // Send to FactFinder using server action
    trackFactFinderAddToCartAction(
      product.id,
      quantity,
      cardPrices.salePrice / quantity,
      product.title,
      product.productMasterSKU
    ).catch((error) => console.error("Error calling trackFactFinderAddToCartAction:", error))
  }
}

export function trackRemoveFromBasket(
  isoLocale: string,
  isB2B: boolean,
  product: ReducedProductData | undefined,
  prices: ProductPriceContainerCtnrROV1 | undefined,
  quantity: number
) {
  if (product && prices && quantity) {
    const cardPrices: CardPrices = determineCardPrices(isoLocale, isB2B, prices, undefined, quantity, product.id)

    const removeFromCartData = {
      event: "removeFromCart",
      ecommerce: {
        currencyCode: product.currency,
        add: {
          products: [
            {
              name: product.title,
              id: determineEffectiveProductId(product.id),
              price: cardPrices.salePrice,
              brand: product.brand,
              category: calculateCategoryPathString(product),
              quantity: "" + quantity,
            },
          ],
        },
      },
    }

    sendGTMEvent(removeFromCartData)
  }
}

export function trackFFProductClick(
  product: ReducedProductData | undefined,
  trackingList: string,
  position: number,
  searchTerm: string | undefined
) {
  if (product) {
    trackFactFinderProductClickAction(product, trackingList, position, searchTerm).then(() => {
      console.log("trackFFProductClick")
    })
  }
}

export function trackProductClick(product: ReducedProductData, trackingList: string, position: number) {
  /*

//For reference: an original event json payload from legacy app

{
    "event": "productClick",
    "ecommerce": {
    "click": {
      "actionField": {
        "list": "Category Resultslist",
          "action": "click"
      },
      "products": [
        {
          "category": "Programm GW-Mailand",
          "id": "A254411",
          "list": "Category Resultslist",
          "name": "Elektrisch höhenverstellbarer Schreibtisch GW-MAILAND 7909",
          "position": 1
        }
      ]
    }
  },
    "gtm.uniqueEventId": 244
  }*/

  const trackedProductId = product.id.toLowerCase().startsWith("e") ? product.id.toUpperCase() : "A" + product.id

  const productClickData = {
    event: "productClick",
    ecommerce: {
      click: {
        actionField: {
          list: trackingList,
          action: "click",
        },
        products: [
          {
            category: product.category ?? "",
            id: trackedProductId,
            list: trackingList,
            name: product.title,
            position: position,
          },
        ],
      },
    },
  }

  // Send to Google Tag Manager only
  // FactFinder tracking is handled by the ProductClickTrackingClient component
  // which calls the trackProductClickAction server action
  sendGTMEvent(productClickData)
}
